/**
 * Contrast & WCAG Utilities
 * 
 * Utilities for calculating WCAG accessibility levels and lightness values
 * from contrast ratios and positions.
 */

import { contrast as leonardoContrast } from '@adobe/leonardo-contrast-colors';
import chroma from 'chroma-js';

// =============================================================================
// TYPES
// =============================================================================

/**
 * WCAG accessibility level based on contrast ratio
 * - FAIL: Below 3:1 - Not accessible
 * - +18px: 3:1 to 4.5:1 - AA for large text only (18pt+ or 14pt bold)
 * - AA: 4.5:1 to 7:1 - AA for normal text
 * - AAA: 7:1+ - AAA (enhanced contrast)
 */
export type WCAGLevel = 'FAIL' | '+18px' | 'AA' | 'AAA';

// =============================================================================
// WCAG LEVEL CALCULATION
// =============================================================================

/**
 * Determine WCAG accessibility level from contrast ratio
 * 
 * Based on WCAG 2.1 guidelines:
 * - Level AAA requires 7:1 for normal text
 * - Level AA requires 4.5:1 for normal text
 * - Level AA for large text requires 3:1 (large = 18pt+ or 14pt bold)
 * - Below 3:1 fails all criteria
 * 
 * @param ratio - WCAG 2.1 contrast ratio (1 to 21)
 * @returns WCAG level string
 * 
 * @example
 * getWCAGLevel(8.5);  // 'AAA'
 * getWCAGLevel(5.2);  // 'AA'
 * getWCAGLevel(3.5);  // '+18px'
 * getWCAGLevel(2.1);  // 'FAIL'
 */
export const getWCAGLevel = (ratio: number): WCAGLevel => {
  if (ratio >= 7) return 'AAA';
  if (ratio >= 4.5) return 'AA';
  if (ratio >= 3) return '+18px';
  return 'FAIL';
};

// =============================================================================
// LIGHTNESS CALCULATION
// =============================================================================

/**
 * Calculate approximate L* (CIE Lab lightness) from contrast value
 * 
 * Works for both WCAG ratios and APCA Lc values by normalizing to slider position.
 * Uses logarithmic scale to match the perceptual distribution of contrast.
 * 
 * For a light theme (lightness > 50):
 *   - Low contrast value = near background lightness (high L*)
 *   - High contrast value = near black (low L*)
 * 
 * For a dark theme (lightness <= 50):
 *   - Low contrast value = near background lightness (low L*)
 *   - High contrast value = near white (high L*)
 * 
 * @param contrastValue - Current contrast value (WCAG ratio 1-21 or APCA Lc 15-106)
 * @param isLightTheme - Whether the current theme is light mode
 * @param minValue - Minimum contrast value (1 for WCAG, 15 for APCA)
 * @param maxValue - Maximum contrast value (21 for WCAG, 106 for APCA)
 * @returns Approximate L* value (0 to 100)
 */
export const calculateLightnessFromRatio = (
  contrastValue: number,
  isLightTheme: boolean,
  minValue: number = 1,
  maxValue: number = 21
): number => {
  // Clamp contrast value to valid range
  const clampedValue = Math.max(minValue, Math.min(maxValue, contrastValue));
  
  // Use logarithmic scale for perceptually even distribution
  // position = log(value/min) / log(max/min), where position ranges 0 to 1
  const logRange = Math.log(maxValue / minValue);
  const position = clampedValue <= minValue ? 0 : Math.log(clampedValue / minValue) / logRange;
  
  // Calculate L* based on theme
  // Light theme: high contrast = darker color (lower L*)
  // Dark theme: high contrast = lighter color (higher L*)
  let lightness: number;
  
  if (isLightTheme) {
    // Light theme: L* goes from ~100 (low contrast) to ~0 (high contrast)
    lightness = 100 - (position * 100);
  } else {
    // Dark theme: L* goes from ~0 (low contrast) to ~100 (high contrast)
    lightness = position * 100;
  }
  
  // Round to 2 decimal places
  return Math.round(lightness * 100) / 100;
};

// =============================================================================
// LEONARDO CONTRAST CALCULATION
// =============================================================================

/**
 * Convert hex color to RGB array for Leonardo's contrast function
 * 
 * @param hex - Hex color string (e.g., "#959595" or "959595")
 * @returns RGB array [r, g, b] with values 0-255
 */
const hexToRGB = (hex: string): [number, number, number] => {
  const cleanHex = hex.replace('#', '');
  const color = chroma(cleanHex) as unknown as chroma.Color;
  const rgb = color.rgb();
  return [rgb[0], rgb[1], rgb[2]];
};

/**
 * Calculate contrast between two colors using Leonardo's contrast function
 * 
 * This allows recalculating contrast for existing colors under different formulas,
 * enabling the UX where colors stay the same but contrast values update when
 * switching between WCAG and APCA.
 * 
 * @param colorHex - Foreground color hex string
 * @param backgroundHex - Background color hex string
 * @param formula - Contrast formula ('wcag2' or 'wcag3')
 * @returns Contrast value (WCAG ratio or APCA Lc)
 * 
 * @example
 * calculateContrast('#959595', '#ffffff', 'wcag2'); // 3.00
 * calculateContrast('#959595', '#ffffff', 'wcag3'); // 56.64
 */
export const calculateContrast = (
  colorHex: string,
  backgroundHex: string,
  formula: 'wcag2' | 'wcag3' = 'wcag2'
): number => {
  const colorRGB = hexToRGB(colorHex);
  const bgRGB = hexToRGB(backgroundHex);
  
  return leonardoContrast(colorRGB, bgRGB, undefined, formula);
};

/**
 * Get actual L* (CIE Lab lightness) from a hex color
 * 
 * Unlike calculateLightnessFromRatio which approximates L* from position,
 * this extracts the actual L* value from the color itself.
 * 
 * @param colorHex - Hex color string
 * @returns L* value (0 to 100)
 * 
 * @example
 * getLightnessFromColor('#959595'); // 61.70
 * getLightnessFromColor('#ffffff'); // 100.00
 */
export const getLightnessFromColor = (colorHex: string): number => {
  const color = chroma(colorHex) as unknown as chroma.Color;
  const lab = color.lab();
  return Math.round(lab[0] * 100) / 100;
};

// =============================================================================
// FORMATTING UTILITIES
// =============================================================================

/**
 * Format contrast ratio for display
 * 
 * @param ratio - Contrast ratio value
 * @returns Formatted string like "4.02:1"
 */
export const formatContrastRatio = (ratio: number): string => {
  return `${ratio.toFixed(2)}:1`;
};

/**
 * Format lightness value for display
 * 
 * @param lightness - L* value (0 to 100)
 * @returns Formatted string like "54.26"
 */
export const formatLightness = (lightness: number): string => {
  return lightness.toFixed(2);
};

